import {t, useLanguage} from '../i18n';
// src/pages/CompleteProfileOnboarding.jsx
// LOOP AUTH OPTIMAL R31 — authenticated profile completion shared by Google, Apple and legacy accounts.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { httpRequestJson } from "../utils/nativeHttp";
import {
  LOOP_PHONE_COUNTRIES,
  cleanPhoneNationalInput,
  parsePhoneValue,
  phoneSnapshotFromParts,
} from "../utils/phoneUtils";
import {
  LOOP_LEGAL_TERMS_VERSION,
  LOOP_LEGAL_PRIVACY_VERSION,
  LOOP_LEGAL_PAYMENTS_VERSION,
  LOOP_LEGAL_SNAPSHOT_VERSION,
} from "../utils/legalVersions";
import {
  getCurrentAuthIdToken,
  getLoopAuthPlatform,
  setProfileCompletionCache,
} from "../auth/socialAuth";
import { getSafeAuthRedirectPath } from "../auth/authNavigation";

const BRAND = "#00675f";
const BRAND_2 = "#00897f";
const LOOP_AUTH_FUNCTIONS_BASE =
  "https://us-central1-web-app-d9799.cloudfunctions.net";
const LOOP_RESOLVE_PROFILE_URL = `${LOOP_AUTH_FUNCTIONS_BASE}/resolveAuthProfileHttpR31`;
const LOOP_COMPLETE_PROFILE_URL = `${LOOP_AUTH_FUNCTIONS_BASE}/completeAuthProfileHttpR31`;

function withTimeout(promise, ms, label = "timeout") {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([
    Promise.resolve(promise).finally(() => {
      try {
        if (timer) clearTimeout(timer);
      } catch (_) {}
    }),
    timeout,
  ]);
}

function splitDisplayName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function normalizeProfile(profile = {}) {
  const split = splitDisplayName(
    profile.fullName || profile.displayName || profile.nombre || "",
  );
  const parsedPhone = parsePhoneValue(
    profile.phoneE164 ||
      profile.phoneNormalized ||
      profile.phoneDisplay ||
      profile.phone ||
      profile.telefono ||
      "",
    { defaultCountry: profile.phoneCountryCode || "PA" },
  );

  return {
    firstName: profile.firstName || profile.nombre || split.firstName || "",
    lastName: profile.lastName || profile.apellido || split.lastName || "",
    email: profile.email || profile.emailNormalized || "",
    countryCode: parsedPhone.dialCode || "+507",
    phone: parsedPhone.nationalFormatted || "",
    birthDate: profile.birthDate || profile.fechaNacimiento || "",
    acceptedTerms: false,
  };
}

function isAdultEnough(dateValue) {
  if (!dateValue) return false;
  const birth = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return false;
  const today = new Date();
  const cutoff = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );
  return birth <= cutoff;
}

function getNextTarget(location) {
  try {
    const params = new URLSearchParams(location?.search || "");
    const queryTarget = params.get("next") || "";
    const stateTarget =
      typeof location?.state?.from === "string" ? location.state.from : "";
    const storedTarget =
      window.localStorage.getItem("loop_post_auth_redirect") ||
      window.sessionStorage.getItem("loop_post_auth_redirect") ||
      "";
    return getSafeAuthRedirectPath(
      queryTarget || stateTarget || storedTarget || "/",
      "/",
    );
  } catch (_) {
    return "/";
  }
}

function profileErrorMessage(error) {
  const raw = String(
    error?.message || error?.code || error || "",
  ).toLowerCase();
  if (raw.includes("underage") || raw.includes("adult")) {
    return "Debes ser mayor de edad para usar Loop.";
  }
  if (raw.includes("phone")) return "Revisa el número de teléfono.";
  if (
    raw.includes("auth-token") ||
    raw.includes("unauthenticated") ||
    raw.includes("401")
  ) {
    return "Tu sesión expiró. Inicia sesión otra vez.";
  }
  if (raw.includes("timeout")) {
    return "La conexión tardó demasiado. Revisa tu internet e intenta nuevamente.";
  }
  return "No pudimos guardar tu perfil. Revisa los datos e intenta nuevamente.";
}

export function isLoopProfileComplete(profile) {
  if (!profile || typeof profile !== "object") return false;
  const firstName = profile.firstName || profile.nombre || profile.displayName;
  const lastName = profile.lastName || profile.apellido;
  const phone =
    profile.phoneE164 ||
    profile.phoneNormalized ||
    profile.phone ||
    profile.telefono;
  const birthDate = profile.birthDate || profile.fechaNacimiento;
  const legal =
    profile.legalAccepted === true ||
    profile.acceptedTerms === true ||
    Boolean(profile.acceptedTermsAt || profile.termsAcceptedAt);
  return Boolean(
    String(firstName || "").trim() &&
      String(lastName || "").trim() &&
      String(phone || "").trim() &&
      String(birthDate || "").trim() &&
      legal,
  );
}

export default function CompleteProfileOnboarding() {
  useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const nextTarget = useMemo(() => getNextTarget(location), [location]);
  const platform = getLoopAuthPlatform();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+507",
    phone: "",
    birthDate: "",
    acceptedTerms: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedCountry = useMemo(
    () =>
      LOOP_PHONE_COUNTRIES.find((item) => item.dialCode === data.countryCode) ||
      LOOP_PHONE_COUNTRIES[0],
    [data.countryCode],
  );

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      try {
        const idToken = await getCurrentAuthIdToken();
        const response = await withTimeout(
          httpRequestJson({
            url: LOOP_RESOLVE_PROFILE_URL,
            method: "POST",
            headers: { Authorization: `Bearer ${idToken}` },
            data: { source: "complete_profile_load_r30" },
          }),
          30000,
          "complete-profile-load-timeout",
        );

        if (!mounted) return;
        if (response?.profileComplete === true) {
          navigate(nextTarget, { replace: true });
          return;
        }
        setData((previous) => ({
          ...previous,
          ...normalizeProfile(response?.profile || {}),
        }));
      } catch (error) {
        console.error("[CompleteProfile R31] load failed", error);
        if (mounted) {
          setStatus({ type: "error", message: profileErrorMessage(error) });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadProfile();
    return () => {
      mounted = false;
    };
  }, [navigate, nextTarget]);

  const update = (key, value) => {
    setStatus({ type: "", message: "" });
    setFieldErrors((previous) => ({ ...previous, [key]: "" }));
    setData((previous) => ({ ...previous, [key]: value }));
  };

  const validate = () => {
    const errors = {};
    const phoneSnapshot = phoneSnapshotFromParts(data.countryCode, data.phone);
    if (!String(data.firstName || "").trim())
      errors.firstName = "Ingresa tu nombre.";
    if (!String(data.lastName || "").trim())
      errors.lastName = "Ingresa tu apellido.";
    if (!phoneSnapshot.phoneE164) errors.phone = "Ingresa un teléfono válido.";
    if (!data.birthDate) errors.birthDate = "Ingresa tu fecha de nacimiento.";
    else if (!isAdultEnough(data.birthDate)) {
      errors.birthDate = "Debes ser mayor de edad para usar Loop.";
    }
    if (!data.acceptedTerms)
      errors.acceptedTerms = "Debes aceptar las condiciones para continuar.";
    setFieldErrors(errors);
    return { ok: Object.keys(errors).length === 0, phoneSnapshot };
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    setStatus({ type: "", message: "" });
    const validation = validate();
    if (!validation.ok) return;

    setSaving(true);
    try {
      const idToken = await getCurrentAuthIdToken({ forceRefresh: false });
      const firstName = String(data.firstName || "").trim();
      const lastName = String(data.lastName || "").trim();
      const response = await withTimeout(
        httpRequestJson({
          url: LOOP_COMPLETE_PROFILE_URL,
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
          data: {
            firstName,
            lastName,
            birthDate: data.birthDate,
            phoneCountryCode: validation.phoneSnapshot.phoneCountryCode,
            phoneDialCode: validation.phoneSnapshot.phoneDialCode,
            phoneNational: validation.phoneSnapshot.phoneNational,
            phoneDisplay: validation.phoneSnapshot.phoneDisplay,
            phoneE164: validation.phoneSnapshot.phoneE164,
            acceptedTerms: true,
            legalTermsVersion: LOOP_LEGAL_TERMS_VERSION,
            legalPrivacyVersion: LOOP_LEGAL_PRIVACY_VERSION,
            legalPaymentsVersion: LOOP_LEGAL_PAYMENTS_VERSION,
            legalSnapshotVersion: LOOP_LEGAL_SNAPSHOT_VERSION,
            platform,
            source: "complete_profile_onboarding_r31",
          },
        }),
        30000,
        "complete-profile-save-timeout",
      );

      if (!response?.ok || response?.profileComplete !== true) {
        throw new Error(response?.message || "profile-completion-failed");
      }

      setProfileCompletionCache(response.uid || "", true);
      try {
        window.localStorage.removeItem("loop_post_auth_redirect");
        window.sessionStorage.removeItem("loop_post_auth_redirect");
        window.dispatchEvent(new Event("loop-profile-completed"));
      } catch (_) {}

      navigate(nextTarget, { replace: true });
    } catch (error) {
      console.error("[CompleteProfile R31] save failed", error);
      setStatus({ type: "error", message: profileErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="complete-profile-page">
        <style>{styles}</style>
        <section
          className="complete-profile-card loading"
          aria-label={t("Cargando perfil")}
        />
      </main>
    );
  }

  return (
    <main className="complete-profile-page">
      <style>{styles}</style>
      <section className="complete-profile-card">
        <div className="complete-profile-brand">∞</div>
        <div className="complete-profile-eyebrow">{t("Un último paso")}</div>
        <h1>{t("Completa tu perfil")}</h1>
        <p className="complete-profile-subtitle">
          Ya creamos tu cuenta. Solo necesitamos estos datos para identificar
          tus reservas y beneficios.
        </p>

        {status.message ? (
          <div
            className={`complete-profile-alert ${status.type === "error" ? "is-error" : "is-success"}`}
          >
            {status.message}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="complete-profile-form">
          <div className="complete-profile-grid">
            <label>
              <span>{t("Nombre")}</span>
              <input
                value={data.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                placeholder={t("Tu nombre")}
                autoComplete="given-name"
                className={fieldErrors.firstName ? "is-error" : ""}
              />
              {fieldErrors.firstName ? (
                <small>{fieldErrors.firstName}</small>
              ) : null}
            </label>
            <label>
              <span>{t("Apellido")}</span>
              <input
                value={data.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                placeholder={t("Tu apellido")}
                autoComplete="family-name"
                className={fieldErrors.lastName ? "is-error" : ""}
              />
              {fieldErrors.lastName ? (
                <small>{fieldErrors.lastName}</small>
              ) : null}
            </label>
            {data.email ? (
              <label className="wide">
                <span>{t("Correo")}</span>
                <input
                  value={data.email}
                  type="email"
                  readOnly
                  className="is-readonly"
                />
              </label>
            ) : null}
            <label>
              <span>{t("País")}</span>
              <select
                value={data.countryCode}
                onChange={(e) => update("countryCode", e.target.value)}
              >
                {LOOP_PHONE_COUNTRIES.map((item) => (
                  <option
                    key={`${item.countryCode}-${item.dialCode}-${item.label}`}
                    value={item.dialCode}
                  >
                    {item.dialCode} · {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t("Teléfono móvil")}</span>
              <input
                value={data.phone}
                onChange={(e) =>
                  update(
                    "phone",
                    cleanPhoneNationalInput(e.target.value, data.countryCode),
                  )
                }
                type="tel"
                placeholder={selectedCountry.placeholder}
                autoComplete="tel-national"
                className={fieldErrors.phone ? "is-error" : ""}
              />
              {fieldErrors.phone ? <small>{fieldErrors.phone}</small> : null}
            </label>
            <label className="wide">
              <span>{t("Fecha de nacimiento")}</span>
              <input
                value={data.birthDate}
                onChange={(e) => update("birthDate", e.target.value)}
                type="date"
                autoComplete="bday"
                className={fieldErrors.birthDate ? "is-error" : ""}
              />
              {fieldErrors.birthDate ? (
                <small>{fieldErrors.birthDate}</small>
              ) : null}
            </label>
          </div>

          <label
            className={`complete-profile-terms ${fieldErrors.acceptedTerms ? "is-error" : ""}`}
          >
            <input
              type="checkbox"
              checked={data.acceptedTerms}
              onChange={(e) => update("acceptedTerms", e.target.checked)}
            />
            <span>{t("Confirmo que soy mayor de edad y acepto los")}{" "}
              <Link to="/terms" target="_blank" rel="noreferrer">{t("Términos")}</Link>{t(", la")}{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer">{t("Política de privacidad")}</Link>{" "}{t("y las")}{" "}
              <Link to="/payments" target="_blank" rel="noreferrer">{t("condiciones de pagos y cancelaciones")}</Link>
              .
            </span>
          </label>
          {fieldErrors.acceptedTerms ? (
            <small className="complete-profile-terms-error">
              {fieldErrors.acceptedTerms}
            </small>
          ) : null}

          <button
            type="submit"
            disabled={saving}
            className="complete-profile-submit"
          >
            {saving ? t("Guardando...") : "Continuar a Loop"}
          </button>
        </form>
      </section>
    </main>
  );
}

const styles = `
.complete-profile-page{min-height:100dvh;display:grid;place-items:center;padding:calc(18px + env(safe-area-inset-top,0px)) 16px calc(24px + env(safe-area-inset-bottom,0px));background:radial-gradient(circle at top left,rgba(0,103,95,.12),transparent 30%),linear-gradient(180deg,#f8fafc 0%,#fff 56%,#f8fafc 100%);color:#0f172a;box-sizing:border-box}.complete-profile-card{width:min(100%,560px);background:rgba(255,255,255,.98);border:1px solid rgba(226,232,240,.96);border-radius:34px;padding:26px;box-shadow:0 28px 80px rgba(15,23,42,.12);box-sizing:border-box}.complete-profile-card.loading{min-height:420px;background:linear-gradient(90deg,#f1f5f9 0%,#fff 50%,#f1f5f9 100%);background-size:200% 100%;animation:completeProfileShimmer 1.2s infinite linear}.complete-profile-brand{width:58px;height:58px;border-radius:22px;display:grid;place-items:center;background:linear-gradient(135deg,${BRAND},${BRAND_2});color:#fff;font-size:29px;font-weight:950;margin-bottom:18px;box-shadow:0 16px 34px rgba(0,103,95,.23)}.complete-profile-eyebrow{color:${BRAND};font-size:12px;font-weight:950;letter-spacing:.13em;text-transform:uppercase;margin-bottom:8px}.complete-profile-card h1{margin:0;color:#0f172a;font-size:clamp(34px,7vw,52px);line-height:.96;letter-spacing:-.07em;font-weight:950}.complete-profile-subtitle{margin:12px 0 0;color:#64748b;font-size:15px;line-height:1.5;font-weight:700}.complete-profile-alert{margin-top:16px;border-radius:20px;padding:13px 15px;font-size:13px;font-weight:850}.complete-profile-alert.is-error{border:1px solid #fecaca;background:#fef2f2;color:#dc2626}.complete-profile-alert.is-success{border:1px solid #bbf7d0;background:#ecfdf5;color:#047857}.complete-profile-form{margin-top:20px}.complete-profile-grid{display:grid;grid-template-columns:1fr 1fr;gap:13px}.complete-profile-grid label{min-width:0}.complete-profile-grid label.wide{grid-column:1/-1}.complete-profile-grid span{display:block;color:#334155;font-size:13px;font-weight:900;margin-bottom:7px}.complete-profile-grid input,.complete-profile-grid select{width:100%;min-height:52px;border-radius:18px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;outline:none;font-size:15px;font-weight:760;padding:0 14px;box-sizing:border-box}.complete-profile-grid input:focus,.complete-profile-grid select:focus{border-color:rgba(0,103,95,.42);box-shadow:0 0 0 4px rgba(0,103,95,.10)}.complete-profile-grid .is-error{border-color:#fecaca;background:#fff7f7}.complete-profile-grid .is-readonly{background:#f8fafc;color:#64748b}.complete-profile-grid small,.complete-profile-terms-error{display:block;margin-top:6px;color:#dc2626;font-size:12px;font-weight:800}.complete-profile-terms{margin-top:16px;border:1px solid #e2e8f0;background:#f8fafc;border-radius:20px;padding:13px;display:flex;align-items:flex-start;gap:11px;color:#475569;font-size:13px;font-weight:800;line-height:1.4}.complete-profile-terms.is-error{border-color:#fecaca;background:#fff7f7}.complete-profile-terms input{margin-top:2px;width:18px;height:18px;accent-color:${BRAND};flex:0 0 auto}.complete-profile-terms a{color:${BRAND};font-weight:950;text-decoration:none}.complete-profile-submit{width:100%;min-height:54px;margin-top:16px;border:0;border-radius:20px;background:linear-gradient(135deg,${BRAND},${BRAND_2});color:#fff;font-size:15px;font-weight:950;box-shadow:0 16px 34px rgba(0,103,95,.23);cursor:pointer}.complete-profile-submit:disabled{opacity:.62;cursor:not-allowed}@media(max-width:560px){.complete-profile-card{padding:22px;border-radius:30px}.complete-profile-grid{grid-template-columns:1fr}.complete-profile-grid label.wide{grid-column:auto}}@keyframes completeProfileShimmer{from{background-position:200% 0}to{background-position:-200% 0}}
`;
